/**
 * TimeFrameSelector - Displays time frame pill buttons for the KPI Overview filter panel.
 * The date range is displayed in the DateFilterPicker (single source of truth),
 * NOT duplicated here.
 */

import React from 'react';
import { Platform, Text, TouchableOpacity, View, StyleSheet } from 'react-native';
import { FontFamily } from '../../../constants/fonts';
import { useTheme } from '../../../theme/useTheme';

export type TimeFrameOption =
	| 'week-to-date'
	| 'period-to-date'
	| 'quarter-to-date'
	| 'year-to-date'
	| 'last-closed-week'
	| 'last-closed-period';

const TIME_FRAME_OPTIONS: { label: string; value: TimeFrameOption }[] = [
	{ label: 'Week-to-Date', value: 'week-to-date' },
	{ label: 'Period-to-Date', value: 'period-to-date' },
	{ label: 'Quarter-to-Date', value: 'quarter-to-date' },
	{ label: 'Year-to-Date', value: 'year-to-date' },
	{ label: 'Last Closed Week', value: 'last-closed-week' },
	{ label: 'Last Closed Period', value: 'last-closed-period' },
];

interface TimeFrameSelectorProps {
	selectedTimeFrame: TimeFrameOption | null;
	onSelect: (value: TimeFrameOption) => void;
	useMobileAbbreviations?: boolean;
	hideLabel?: boolean;
}

const TimeFrameSelector: React.FC<TimeFrameSelectorProps> = ({
	selectedTimeFrame,
	onSelect,
	useMobileAbbreviations = false,
	hideLabel = false,
}) => {
	const { colors, resolvedTheme } = useTheme();
	const isDark = resolvedTheme === 'dark';
	const mobileLabelMap: Record<TimeFrameOption, string> = {
		'last-closed-week': 'LCW',
		'last-closed-period': 'LCP',
		'period-to-date': 'PTD',
		'week-to-date': 'WTD',
		'quarter-to-date': 'QTD',
		'year-to-date': 'YTD',
	};

	return (
		<View style={tfStyles.container}>
			{!hideLabel ? <Text style={[tfStyles.label, isDark && { color: colors.text.secondary }]}>TIME FRAME</Text> : null}
			<View style={tfStyles.pillsRow}>
				{TIME_FRAME_OPTIONS.map(option => {
					const isActive = selectedTimeFrame === option.value;
					const displayLabel = useMobileAbbreviations
						? mobileLabelMap[option.value] ?? option.label
						: option.label;
					return (
						<TouchableOpacity
							key={option.value}
							style={[
								tfStyles.pill,
								isActive && tfStyles.pillActive,
								isDark && !isActive && { backgroundColor: colors.background.elevated, borderColor: colors.border.default },
							]}
							onPress={() => onSelect(option.value)}
							accessibilityRole="button"
							accessibilityLabel={`Select ${option.label} time frame`}
						>
							<Text style={[tfStyles.pillText, isActive && tfStyles.pillTextActive, isDark && !isActive && { color: colors.text.primary }]}>
								{displayLabel}
							</Text>
						</TouchableOpacity>
					);
				})}
			</View>
		</View>
	);
};

const tfStyles = StyleSheet.create({
	container: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 12,
		flexWrap: 'wrap',
	},
	label: {
		fontSize: 12,
		fontFamily: FontFamily.bold,
		fontWeight: '700',
		color: '#6A6D71',
		letterSpacing: 0.4,
	},
	pillsRow: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 8,
		flexWrap: 'wrap',
	},
	pill: {
		paddingHorizontal: 14,
		paddingVertical: 8,
		borderRadius: 20,
		borderWidth: 1,
		borderColor: '#E3E9F3',
		backgroundColor: '#FFFFFF',
	},
	pillActive: {
		backgroundColor: '#0B5FC6',
		borderColor: '#0B5FC6',
	},
	pillText: {
		fontSize: 13,
		fontFamily: FontFamily.medium,
		color: '#3D4147',
	},
	pillTextActive: {
		color: '#FFFFFF',
	},
});

export default TimeFrameSelector;
